import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Typography,
  Link,
  makeStyles,
  Box,
  Switch,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import DescriptionIcon from '@material-ui/icons/Description';
import FolderIcon from '@material-ui/icons/Folder';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import { DriveFolder, DriveCompany } from 'lib/Model/Drive';
import { createDriveFolder } from 'lib/Api/Drive';
import {
  generateQuoteSlides,
  saveQuote,
  fetchZendeskUsers,
} from 'lib/Api/QuoteGenerator';
import { QuoteResponse, ZendeskUsers } from 'lib/Model/QuoteGenerator';
import { getUserData } from 'lib/Helper/Auth';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    // minWidth removed; dialogs use fullWidth + maxWidth="sm" instead
  },
  formControl: {
    margin: theme.spacing(1, 0, 2),
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  folderSearchBox: {
    marginTop: theme.spacing(2),
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  successLink: {
    marginTop: theme.spacing(2),
  },
  successActions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  zendeskOptions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  folderCreationOptions: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(4),
  },
  identifierInput: {
    marginTop: theme.spacing(1),
  },
}));

interface QuoteGenerationDialogProps {
  open: boolean;
  quoteData: QuoteResponse | null;
  companyName: string;
  onClose: () => void;
  onGenerationSuccess?: (savedQuoteId: number) => void;
  proposalsFolderId?: string | null;
  selectedCompany?: DriveCompany | null;
}

export const QuoteGenerationDialog: React.FC<QuoteGenerationDialogProps> = ({
  open,
  quoteData,
  companyName,
  onClose,
  onGenerationSuccess,
  proposalsFolderId,
  selectedCompany,
}) => {
  const classes = useStyles();
  const [generationMethod, setGenerationMethod] = useState<
    'drive' | 'download' | 'create_drive_folder'
  >(proposalsFolderId ? 'drive' : 'create_drive_folder');
  const [loading, setLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<DriveFolder | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [generatedFolderId, setGeneratedFolderId] = useState<string | null>(
    null,
  );
  const [actualSavedQuoteId, setActualSavedQuoteId] = useState<number | null>(
    null,
  );
  const [createZendeskDeal, setCreateZendeskDeal] = useState<boolean>(false);
  const [zendeskUsers, setZendeskUsers] = useState<ZendeskUsers[]>([]);
  const [selectedZendeskOwner, setSelectedZendeskOwner] =
    useState<ZendeskUsers | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [estimatedCloseDate, setEstimatedCloseDate] = useState<string>('');
  const [dealName, setDealName] = useState<string>('');
  const [zendeskDealId, setZendeskDealId] = useState<number | null>(null);
  const [zendeskUsersFetched, setZendeskUsersFetched] =
    useState<boolean>(false);

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folderCreationCompleted, setFolderCreationCompleted] = useState(false);
  const [identifierTypeForNewFolder, setIdentifierTypeForNewFolder] = useState<
    'registration' | 'vat' | 'unknown'
  >('registration');
  const [identifierValueForNewFolder, setIdentifierValueForNewFolder] =
    useState('');
  const [
    actualProposalsFolderIdFromCreation,
    setActualProposalsFolderIdFromCreation,
  ] = useState<string | null>(null);
  const [identifierValidationError, setIdentifierValidationError] =
    useState<string>('');

  const user = getUserData();

  const validateIdentifier = (value: string): boolean => {
    if (!value.trim()) return true;
    const hasNumbers = /\d/.test(value);
    const hasLetters = /[a-zA-Z]/.test(value);
    return hasNumbers && hasLetters;
  };

  useEffect(() => {
    if (open) {
      setGenerationMethod(proposalsFolderId ? 'drive' : 'create_drive_folder');
      setError(null);
      setSuccess(false);
      setDownloadLink(null);
      setDocumentId(null);
      setGeneratedFolderId(null);
      setActualProposalsFolderIdFromCreation(null);
      setIsCreatingFolder(false);
      setFolderCreationCompleted(false);

      if (proposalsFolderId) {
        setSelectedFolder({
          id: proposalsFolderId,
          folder_name: `${companyName} - Proposals`,
          proposals_folder_id: proposalsFolderId,
        });
      } else {
        setSelectedFolder(null);
      }

      if (selectedCompany) {
        if (selectedCompany.CNumber) {
          setIdentifierTypeForNewFolder('registration');
          setIdentifierValueForNewFolder(selectedCompany.CNumber);
        } else if (selectedCompany.VAT_number) {
          setIdentifierTypeForNewFolder('vat');
          setIdentifierValueForNewFolder(selectedCompany.VAT_number);
        } else {
          setIdentifierTypeForNewFolder('unknown');
          setIdentifierValueForNewFolder('');
        }
      } else {
        setIdentifierTypeForNewFolder('registration');
        setIdentifierValueForNewFolder('');
      }

      setGeneratedFolderId(null);
      setActualSavedQuoteId(null);
      setCreateZendeskDeal(false);
      setSelectedZendeskOwner(null);
      setEstimatedCloseDate('');
      setDealName('');
      setZendeskDealId(null);
      setZendeskUsersFetched(false);
      setZendeskUsers([]);
    }
  }, [open, proposalsFolderId, companyName, selectedCompany]);

  useEffect(() => {
    if (createZendeskDeal && !zendeskUsersFetched) {
      const getUsers = async () => {
        setLoadingUsers(true);
        try {
          const response = await fetchZendeskUsers();
          setZendeskUsers(response);
          setZendeskUsersFetched(true);

          if (user?.sub) {
            const userEmail = user.sub;
            const matchedUser = response.find(
              (zu) =>
                zu.email && zu.email.toLowerCase() === userEmail.toLowerCase(),
            );
            if (matchedUser) {
              setSelectedZendeskOwner(matchedUser);
            }
          }
        } catch (err) {
          setError('Failed to load Zendesk users. Please try again.');
          setZendeskUsersFetched(true);
        } finally {
          setLoadingUsers(false);
        }
      };

      getUsers();
    }
  }, [createZendeskDeal, zendeskUsersFetched, user]);

  const handleGenerate = async () => {
    if (!quoteData) {
      setError('Proposal data is not available.');
      return;
    }

    if (
      generationMethod === 'create_drive_folder' &&
      (identifierTypeForNewFolder === 'registration' ||
        identifierTypeForNewFolder === 'vat') &&
      !identifierValueForNewFolder.trim()
    ) {
      setError(
        `The ${
          identifierTypeForNewFolder === 'registration'
            ? 'Registration Number'
            : 'VAT Number'
        } cannot be empty when selected for folder creation.`,
      );
      return;
    }

    if (
      generationMethod === 'create_drive_folder' &&
      (identifierTypeForNewFolder === 'registration' ||
        identifierTypeForNewFolder === 'vat') &&
      identifierValueForNewFolder.trim() &&
      !validateIdentifier(identifierValueForNewFolder.trim())
    ) {
      setError(
        `The ${
          identifierTypeForNewFolder === 'registration'
            ? 'Registration Number'
            : 'VAT Number'
        } must contain both letters and numbers.`,
      );
      return;
    }

    if (createZendeskDeal && !selectedZendeskOwner) {
      setError('Please select a Zendesk owner for the deal.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setGeneratedFolderId(null);
    setActualProposalsFolderIdFromCreation(null);
    setIsCreatingFolder(false);
    setFolderCreationCompleted(false);
    let savedQuoteId: number | null = null;

    try {
      const savedQuoteResponse = await saveQuote(quoteData);
      savedQuoteId = savedQuoteResponse.quote_id;
      setActualSavedQuoteId(savedQuoteId);

      let targetFolderIdForSlides: string | undefined = undefined;

      if (generationMethod === 'create_drive_folder') {
        setIsCreatingFolder(true);
        let regNumForFolderCreation = '';
        if (
          identifierTypeForNewFolder === 'registration' ||
          identifierTypeForNewFolder === 'vat'
        ) {
          regNumForFolderCreation = identifierValueForNewFolder.trim();
        }

        const companyIdForFolder = selectedCompany?.id || undefined;

        const newFolderDataResponse = await createDriveFolder(
          companyName,
          regNumForFolderCreation,
          companyIdForFolder,
        );
        setIsCreatingFolder(false);
        setFolderCreationCompleted(true);

        const proposalsFolder = newFolderDataResponse.data.created_folders.find(
          (f) => f.name === 'Presentations & Proposals',
        );
        if (proposalsFolder) {
          targetFolderIdForSlides = proposalsFolder.id;
          setActualProposalsFolderIdFromCreation(proposalsFolder.id);
          setGeneratedFolderId(proposalsFolder.id);
        } else {
          targetFolderIdForSlides =
            newFolderDataResponse.data.created_folders[0]?.id;
          setGeneratedFolderId(
            newFolderDataResponse.data.created_folders[0]?.id,
          );
        }
      } else if (generationMethod === 'drive') {
        targetFolderIdForSlides =
          selectedFolder?.proposals_folder_id ||
          selectedFolder?.id ||
          undefined;
        if (targetFolderIdForSlides) {
          setGeneratedFolderId(targetFolderIdForSlides);
        }
      }

      const payload: {
        folder_id?: string;
        zendeskOwner?: number;
        estimatedCloseDate?: string;
        isCspProposal?: boolean;
        dealName?: string;
      } = {};

      if (targetFolderIdForSlides) {
        payload.folder_id = targetFolderIdForSlides;
      }

      if (createZendeskDeal && selectedZendeskOwner) {
        payload.zendeskOwner = parseInt(
          selectedZendeskOwner.owner_id.toString(),
          10,
        );
      }
      if (createZendeskDeal && estimatedCloseDate) {
        payload.estimatedCloseDate = estimatedCloseDate;
      }
      if (createZendeskDeal && dealName.trim()) {
        payload.dealName = dealName.trim();
      }

      if (quoteData.isCspProposal) {
        payload.isCspProposal = true;
      }

      const result = await generateQuoteSlides(savedQuoteId, payload);

      if (result.download_link) {
        setDownloadLink(result.download_link);
      } else if (result.document_id && payload.folder_id) {
        // generatedFolderId is already set above for drive or create_drive_folder
      }

      if (result.deal_id) {
        setZendeskDealId(result.deal_id);
      }

      setDocumentId(result.document_id);
      setSuccess(true);

      if (onGenerationSuccess && savedQuoteId) {
        onGenerationSuccess(savedQuoteId);
      }
    } catch (err) {
      let errorMessage =
        err instanceof Error ? err.message : 'Failed to generate Proposal';
      if (savedQuoteId) {
        errorMessage = `Proposal (ID: ${savedQuoteId}) was saved, but slide generation failed: ${errorMessage}. You might need to generate slides separately. Reach out to the insights team for help.`;
      } else {
        errorMessage = `Failed to save proposal: ${errorMessage}`;
      }
      setError(errorMessage);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleIdentifierTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newType = event.target.value as 'registration' | 'vat' | 'unknown';
    setIdentifierTypeForNewFolder(newType);
    setIdentifierValidationError('');
    if (selectedCompany) {
      if (newType === 'registration') {
        setIdentifierValueForNewFolder(selectedCompany.CNumber || '');
      } else if (newType === 'vat') {
        setIdentifierValueForNewFolder(selectedCompany.VAT_number || '');
      } else {
        setIdentifierValueForNewFolder('');
      }
    } else {
      setIdentifierValueForNewFolder('');
    }
  };

  const handleIdentifierValueChange = (value: string) => {
    setIdentifierValueForNewFolder(value);
    if (value.trim() && !validateIdentifier(value.trim())) {
      setIdentifierValidationError(
        `${
          identifierTypeForNewFolder === 'registration'
            ? 'Registration Number'
            : 'VAT Number'
        } must contain both letters and numbers.`,
      );
    } else {
      setIdentifierValidationError('');
    }
  };

  if (loading) {
    const showDocStep =
      generationMethod !== 'create_drive_folder' || folderCreationCompleted;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        disableBackdropClick
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generating Proposal</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box sx={{ width: '100%', my: 2 }}>
            {generationMethod === 'create_drive_folder' && (
              <Box
                display="flex"
                alignItems="center"
                mb={showDocStep ? 1 : 0}
                py={1}
              >
                {isCreatingFolder ? (
                  <CircularProgress size={20} style={{ marginRight: '8px' }} />
                ) : folderCreationCompleted ? (
                  <CheckCircleOutlineIcon
                    style={{ color: 'green', marginRight: '8px' }}
                  />
                ) : (
                  <HourglassEmptyIcon style={{ marginRight: '8px' }} />
                )}
                <Typography variant="body1">
                  {isCreatingFolder
                    ? 'Creating Google Drive folder...'
                    : folderCreationCompleted
                    ? 'Google Drive folder created!'
                    : 'Google Drive folder: Pending'}
                </Typography>
              </Box>
            )}

            {showDocStep && (
              <Box display="flex" alignItems="center" py={1}>
                {loading && !isCreatingFolder ? (
                  <CircularProgress size={20} style={{ marginRight: '8px' }} />
                ) : (
                  <HourglassEmptyIcon style={{ marginRight: '8px' }} />
                )}
                <Typography variant="body1">
                  {loading && !isCreatingFolder
                    ? 'Generating proposal document...'
                    : 'Proposal document: Pending'}
                </Typography>
              </Box>
            )}
          </Box>
          <DialogContentText
            align="center"
            style={{ marginTop: '16px', fontSize: '0.9em', color: '#666' }}
          >
            This process can take some time. Please wait.
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }

  if (success) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Proposal Generated Successfully!</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.successContainer}>
            <Typography variant="body2" style={{ marginTop: '8px' }}>
              Your quote ID is: <strong>{actualSavedQuoteId}</strong>
            </Typography>
            {downloadLink && (
              <Link
                href={downloadLink}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.successLink}
              >
                Click here to download your proposal. Please be sure to upload
                it to the correct Drive folder for visibility.
              </Link>
            )}
            {!downloadLink && documentId && (
              <>
                <Typography variant="body1" className={classes.successLink}>
                  Your proposal has been saved
                  {generationMethod === 'create_drive_folder'
                    ? ` to the newly created Drive folder (${
                        actualProposalsFolderIdFromCreation
                          ? '"Presentations & Proposals"'
                          : 'main company folder'
                      })`
                    : generationMethod === 'drive'
                    ? ' to the selected Drive folder'
                    : ''}
                  {zendeskDealId ? ' and a deal has been created' : ''}.
                </Typography>
                <Box className={classes.successActions}>
                  <Button
                    variant="outlined"
                    href={`https://docs.google.com/presentation/d/${documentId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<DescriptionIcon />}
                  >
                    Open File
                  </Button>
                  {generatedFolderId && (
                    <Button
                      variant="outlined"
                      href={`https://drive.google.com/drive/folders/${generatedFolderId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<FolderIcon />}
                    >
                      Open Folder
                    </Button>
                  )}
                  {createZendeskDeal && zendeskDealId && (
                    <Button
                      variant="outlined"
                      href={`https://konnekttalexio.zendesk.com/sales/deals/${zendeskDealId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<AttachMoneyIcon />}
                    >
                      Open Zendesk Deal
                    </Button>
                  )}
                </Box>
              </>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginTop: '16px' }}
            >
              Remember to review the generated proposal for accuracy before
              sharing it with the client.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Generate Proposal</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          How do you want to save the proposal for {companyName}?
        </DialogContentText>

        {error && (
          <Alert
            severity="error"
            style={{ marginTop: '16px', marginBottom: '16px' }}
          >
            {error}
          </Alert>
        )}

        {quoteData && (
          <FormControl component="fieldset" className={classes.formControl}>
            <FormControlLabel
              control={
                <Switch
                  checked={createZendeskDeal}
                  onChange={(e) => setCreateZendeskDeal(e.target.checked)}
                  name="createZendeskDeal"
                  color="primary"
                />
              }
              label="Create Deal on Zendesk for this proposal"
            />

            {createZendeskDeal && (
              <div className={classes.zendeskOptions}>
                {loadingUsers ? (
                  <Box display="flex" justifyContent="center">
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Autocomplete
                      options={zendeskUsers}
                      getOptionLabel={(option) => option.cam}
                      value={selectedZendeskOwner}
                      onChange={(_, newValue) =>
                        setSelectedZendeskOwner(newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Zendesk Owner"
                          variant="outlined"
                          fullWidth
                          required
                          placeholder="Select an owner"
                        />
                      )}
                    />
                    <TextField
                      label="Estimated Close Date"
                      type="date"
                      value={estimatedCloseDate}
                      onChange={(e) => setEstimatedCloseDate(e.target.value)}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText="This field can be left blank."
                    />
                    <TextField
                      label="Deal Name"
                      value={dealName}
                      onChange={(e) => setDealName(e.target.value)}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder={`${companyName} - Talexio Proposal (Proposal #...)`}
                      helperText="Leave blank to use the default name."
                    />
                  </>
                )}
              </div>
            )}
          </FormControl>
        )}

        <FormControl component="fieldset" className={classes.formControl}>
          <RadioGroup
            value={generationMethod}
            onChange={(e) =>
              setGenerationMethod(
                e.target.value as 'drive' | 'download' | 'create_drive_folder',
              )
            }
          >
            <FormControlLabel
              value="drive"
              control={<Radio />}
              label="Save to existing Google Drive folder"
              disabled={!proposalsFolderId}
            />
            <FormControlLabel
              value="create_drive_folder"
              control={<Radio />}
              label="Create new Google Drive folder and save"
              disabled={!!proposalsFolderId}
            />
          </RadioGroup>
        </FormControl>

        {generationMethod === 'drive' &&
          (proposalsFolderId && selectedFolder?.id === proposalsFolderId ? (
            <Alert
              severity="success"
              style={{ marginTop: '16px', marginBottom: '16px' }}
            >
              The proposal will be saved to the company's 'Presentations &
              Proposals' Drive subfolder automatically.
            </Alert>
          ) : (
            <Alert
              severity="info"
              style={{ marginTop: '16px', marginBottom: '16px' }}
            >
              A "Proposals" folder specific to this company was not
              automatically found. If you need to save to an existing specific
              folder, ensure it's correctly linked in the CRM. Otherwise, you
              can choose to 'Create new Google Drive folder' or 'Download
              directly'.
            </Alert>
          ))}

        {generationMethod === 'create_drive_folder' && (
          <Box className={classes.folderCreationOptions}>
            <Typography variant="subtitle2" gutterBottom>
              Folder Identifier:
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                aria-label="identifier-type-for-new-folder"
                name="identifier-type-for-new-folder"
                value={identifierTypeForNewFolder}
                onChange={handleIdentifierTypeChange}
                row
              >
                <FormControlLabel
                  value="registration"
                  control={<Radio />}
                  label="Registration No."
                />
                <FormControlLabel
                  value="vat"
                  control={<Radio />}
                  label="VAT No."
                />
                <FormControlLabel
                  value="unknown"
                  control={<Radio />}
                  label="Unknown"
                />
              </RadioGroup>
            </FormControl>

            {(identifierTypeForNewFolder === 'registration' ||
              identifierTypeForNewFolder === 'vat') && (
              <TextField
                className={classes.identifierInput}
                variant="outlined"
                label={
                  identifierTypeForNewFolder === 'registration'
                    ? 'Registration Number'
                    : 'VAT Number'
                }
                value={identifierValueForNewFolder}
                onChange={(e) => handleIdentifierValueChange(e.target.value)}
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                error={!!identifierValidationError}
                helperText={
                  identifierValidationError ||
                  (selectedCompany
                    ? `Prefilled from Zendesk. Current value: ${
                        identifierTypeForNewFolder === 'registration'
                          ? selectedCompany.CNumber || 'N/A'
                          : selectedCompany.VAT_number || 'N/A'
                      }`
                    : 'Enter the identifier for the new folder name.')
                }
              />
            )}
            {generationMethod === 'create_drive_folder' &&
              (identifierTypeForNewFolder === 'registration' ||
                identifierTypeForNewFolder === 'vat') &&
              identifierValueForNewFolder.trim() &&
              /^\d+$/.test(identifierValueForNewFolder.trim()) && (
                <Alert
                  severity="error"
                  style={{ marginTop: '8px', marginBottom: '8px' }}
                >
                  The entered{' '}
                  {identifierTypeForNewFolder === 'registration'
                    ? 'Registration Number'
                    : 'VAT Number'}{' '}
                  consists only of numbers. Please add letters to create a valid
                  identifier.
                </Alert>
              )}
            {generationMethod === 'create_drive_folder' &&
              (identifierTypeForNewFolder === 'registration' ||
                identifierTypeForNewFolder === 'vat') &&
              identifierValueForNewFolder.trim() &&
              /^[a-zA-Z]+$/.test(identifierValueForNewFolder.trim()) && (
                <Alert
                  severity="error"
                  style={{ marginTop: '8px', marginBottom: '8px' }}
                >
                  The entered{' '}
                  {identifierTypeForNewFolder === 'registration'
                    ? 'Registration Number'
                    : 'VAT Number'}{' '}
                  consists only of letters. Please add numbers to create a valid
                  identifier.
                </Alert>
              )}
            <Typography variant="body2" style={{ marginTop: '8px' }}>
              The main company folder will be named:{' '}
              <strong>
                {identifierTypeForNewFolder !== 'unknown' &&
                identifierValueForNewFolder.trim()
                  ? `${identifierValueForNewFolder.trim()} - ${companyName}`
                  : companyName}
              </strong>
              . The proposal will be saved in its "Presentations & Proposals"
              subfolder.
            </Typography>
            {identifierTypeForNewFolder === 'unknown' && (
              <Alert severity="warning" style={{ marginTop: '16px' }}>
                <strong>
                  It is strongly recommended to add a unique identifier
                  (Registration Number or VAT Number) to avoid confusion with
                  other companies.
                </strong>{' '}
                Please ensure the company name is distinct and add the
                identifier to the folder name manually later if it becomes
                available.
              </Alert>
            )}
            <Alert severity="info" style={{ marginTop: '16px' }}>
              A new Google Drive folder structure will be created for{' '}
              {companyName}, and the proposal will be saved in its
              "Presentations & Proposals" subfolder.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleGenerate}
          color="primary"
          variant="contained"
          disabled={
            loading ||
            (generationMethod === 'drive' && !proposalsFolderId) ||
            (generationMethod === 'create_drive_folder' &&
              identifierTypeForNewFolder !== 'unknown' &&
              (identifierTypeForNewFolder === 'registration' ||
                identifierTypeForNewFolder === 'vat') &&
              (!identifierValueForNewFolder.trim() ||
                !!identifierValidationError)) ||
            (createZendeskDeal && !selectedZendeskOwner)
          }
        >
          Generate Proposal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteGenerationDialog;
